import React, {useEffect, useLayoutEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import dayjs from "dayjs";
import {Switch} from "@headlessui/react";
import {ReservationPlan} from "types/entities/ReservationPlan";
import {Reservation} from "types/entities/Reservation";
import {calculateReservationPlanTimepickerHoursAndMinutes} from "utils/reservationUtils";
import clsx from "clsx";
import DatePicker, {registerLocale, setDefaultLocale} from "react-datepicker";
import {Portal} from "react-overlays";
import {declareLocaleByConfig} from "utils/localeUtils";
import {LOCALE} from "helpers/config";

interface ReservationPlanListRowProps {
    plan: ReservationPlan;
    onChange: (plan: ReservationPlan) => void;
    reservation: Reservation;
}

const ReservationPlanListRow = (props: ReservationPlanListRowProps) => {
    const {t} = useTranslation();
    const myHeightRef: React.RefObject<HTMLTextAreaElement> = React.createRef();
    const [height, setHeight] = useState(20);
    const [isActive, setIsActive] = useState(props.plan.is_active ? props.plan.is_active : false);
    const [mutated, setMutated] = useState(false);
    const reservationStartTime = props.reservation.date_time ?? dayjs(props.reservation.date).hour(props.reservation.hour).minute(props.reservation.minute).toISOString();
    const startHourBefore = 0;
    const minuteIntervals = 30;
    const timepickerSettings = calculateReservationPlanTimepickerHoursAndMinutes(reservationStartTime, startHourBefore, minuteIntervals);
    const [startTime, setStartTime] = useState<number>(props.plan.time === 0 ? timepickerSettings.defaultHour * 3600 : props.plan.time);
    const generateDateForPicker = (newTime) => {
        const dateForPicker = new Date();
        dateForPicker.setHours(Math.floor(newTime / 3600), (newTime % 3600) / 60, 0);
        return dateForPicker;
    }
    const [startTimeForPicker, setStartTimeForPicker] = useState<any>(generateDateForPicker(startTime));
    const datePickerLocale = declareLocaleByConfig(LOCALE);
    registerLocale("locale", datePickerLocale);
    setDefaultLocale(datePickerLocale);

    const durationPickerHandler = (time: any) => {
        setStartTime(time.toString());
        setValueHandler(props.plan.id, "time", time.toString());
    };

    const handleTimeChange = (date: Date | null) => {
        if (date) {
            const secondsFromMidnight = date.getHours() * 3600 + date.getMinutes() * 60;
            durationPickerHandler(secondsFromMidnight);
        }
    };

    useLayoutEffect(() => {
        if (myHeightRef && myHeightRef.current && myHeightRef.current.scrollHeight > 0) {
            setMutated(false);
            setHeight(myHeightRef.current.scrollHeight);
            return;
        }
        setHeight(20);
    }, [mutated, myHeightRef, myHeightRef.current]);

    const setIsActiveHandler = e => {
        setIsActive(e);
        setValueHandler(props.plan.id, "is_active", e);
    };

    const setValueHandler = (id, name, value) => {
        props.plan[name] = value;
        props.onChange(props.plan);
    };

    const ReservationPlanContainer = ({children}) => {
        const el = document.getElementById("reservation-plan-portal");
        return <Portal container={el}>{children}</Portal>;
    };

    useEffect(() => {
        setStartTimeForPicker(generateDateForPicker(startTime));
    }, [startTime]);

    return (
        <div className={"ReservationPlanFormRow border p-2 rounded shadow-sm bg-white"}>
            <form className={"flex flex-row space-x-2"}>
                <div className={"flex self-start relative"}>
                    <DatePicker
                        className={clsx(
                            "w-full px-3 py-0.5 leading-normal z-1 rounded border min-h-[32px]",
                            props.plan.time ? "text-blue-400" : "text-gray-500 bg-gray-100",
                        )}
                        onChange={handleTimeChange}
                        popperClassName={"!z-[50] [&>div>div>div]:!left-0 [&>div>div>div]:!right-0 [&>div>div>div]:!mx-auto"}
                        popperContainer={ReservationPlanContainer}
                        selected={startTimeForPicker}
                        dateFormat={"HH:mm"}
                        showTimeSelect
                        showTimeSelectOnly
                        timeIntervals={10}
                        timeCaption={t("Time")}
                        id={"time"}
                    />
                </div>

                <div className={"flex w-full"}>
                    <textarea
                        ref={myHeightRef}
                        className={"border w-full rounded px-2 py-1 placeholder:text-gray-500 overflow-y-auto resize-none scrollbar-y-hidden"}
                        defaultValue={props.plan.name}
                        id={"name"}
                        name={"name"}
                        placeholder={t("Plan name")}
                        style={{
                            height: height + "px",
                        }}
                        onChange={e => {
                            setMutated(true);
                            setValueHandler(props.plan.id, "name", e.currentTarget.value);
                        }}
                    />
                </div>
                <div className={"flex items-center justify-end"}>
                    <Switch
                        id={"active"}
                        checked={isActive}
                        onChange={setIsActiveHandler}
                        className={`${isActive ? "bg-blue-400" : "bg-gray-500"} relative inline-flex h-6 w-11 items-center rounded-full`}
                    >
                        <span className="sr-only">{t("Active")}</span>
                        <span className={`${isActive ? "translate-x-6" : "translate-x-1"} inline-block h-4 w-4 transform rounded-full bg-white transition`} />
                    </Switch>
                </div>
            </form>
        </div>
    );
};

export default ReservationPlanListRow;
